/* El precio del gasoil que pagamos: el que declara Carlos Becerra.
 *
 * Igna, 04/10/2026: sólo el valor que declara Carlos Becerra. Llega un mail
 * con asunto «Precio D-500 Otamendi Caleta», FlotaBot lo lee y guarda una
 * fila en `ypf_precio_pagado` con `origen = 'declarado'` (ver la migración
 * 20261004120000_ypf_precio_declarado.sql). Ese valor ES lo que paga Nico, tal
 * cual: no se le descuenta nada.
 *
 * Por eso acá ya no hay simulador de YPF Ruta, ni localidades, ni el 4% de
 * `ypf_descuento`, ni Infinia ni naftas: «a partir de hoy sólo queremos que
 * aparezca ese valor». La misma tabla sigue guardando los avisos de YPF
 * (`origen = 'aviso_ypf'`, precio de surtidor) como registro, y por eso todo
 * lo de acá filtra por origen: un aviso mezclado en el escalón sería un precio
 * de surtidor haciéndose pasar por lo que pagamos.
 *
 * Es una función escalón: «vigente desde el 04/10 10:00» quiere decir que ese
 * precio vale desde ahí hasta que Carlos declare el siguiente. El precio de un
 * momento es el de la última fila con `vigente_desde` <= ese momento.
 */

/* La estación y el producto que declara Carlos: Otamendi y Compañía SRL,
 * Caleta Olivia (cód. 001501), gasoil D-500 (código YPF 08). Se filtra por
 * código y no por `descripcion` («D.DIESEL500»): el texto lo escribe el bot a
 * partir del mail y puede cambiar; el código no. */
export const ESTACION = "001501";
export const PRODUCTO = "08";
export const ORIGEN = "declarado";

/* El código de estación sin ceros adelante. El bot escribe «001501», pero si
 * un día llega «01501» (como lo nombraba el simulador) es la misma estación, y
 * comparados como texto no coincidirían nunca. «0» sigue siendo «0». */
export function codEess(cod) {
  return String(cod ?? "")
    .trim()
    .replace(/^0+(?=\d)/, "");
}

/* Argentina es UTC-3 todo el año (no hay horario de verano desde 2009). La
 * cuenta va a mano y no con `Intl` + `timeZone` para que la hora no dependa de
 * la zona de la máquina ni del ICU del navegador: «vigente desde 04/10 10:00»
 * tiene que decir lo mismo que el mail de Carlos. */
const AR = -3 * 60 * 60 * 1000;

/* Un momento en milisegundos: un Date o un timestamp ISO (`vigente_desde`). */
export function instante(x) {
  if (x instanceof Date) return x.getTime();
  return Date.parse(String(x ?? ""));
}

const dd = (n) => String(n).padStart(2, "0");

/* «04/10 10:00» en hora argentina; con `{ anio: true }`, «04/10/26 10:00»
 * (para el historial, que va a cruzar fin de año). Fecha ilegible: vacío, no
 * «NaN/NaN». */
export function fechaHoraAR(ts, { anio = false } = {}) {
  const ms = instante(ts);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms + AR);
  const a = anio ? `/${String(d.getUTCFullYear()).slice(2)}` : "";
  return `${dd(d.getUTCDate())}/${dd(d.getUTCMonth() + 1)}${a} ${dd(d.getUTCHours())}:${dd(d.getUTCMinutes())}`;
}

const centavos = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/* Las filas declaradas del D-500 de Otamendi, de la más vieja a la más nueva.
 *
 * Se queda SÓLO con `origen = 'declarado'`, aunque el service ya las pida así:
 * esta función es la que decide qué es «lo que pagamos», y no puede depender
 * de que quien la llama haya filtrado bien. Un aviso de YPF que se cuele es un
 * precio de surtidor sin descuento mostrado como si fuera lo que paga Nico.
 *
 * Se descartan las filas sin fecha o sin precio legibles: no se puede decir
 * desde cuándo valen ni cuánto. Con dos en el mismo instante desempata `id`,
 * para que el orden no dependa de cómo llegaron. */
export function declarados(filas) {
  const cod = codEess(ESTACION);
  return (filas || [])
    .filter(
      (f) =>
        f?.origen === ORIGEN &&
        codEess(f.cod_eess) === cod &&
        String(f.producto ?? "").trim() === PRODUCTO &&
        !Number.isNaN(instante(f.vigente_desde)) &&
        Number.isFinite(Number(f.precio)) &&
        f.precio !== null &&
        f.precio !== "",
    )
    .map((f) => ({ ...f, precio: Number(f.precio) }))
    .sort(
      (a, b) =>
        instante(a.vigente_desde) - instante(b.vigente_desde) ||
        (Number(a.id) || 0) - (Number(b.id) || 0),
    );
}

/* Todo lo que la pantalla necesita, de una pasada:
 *
 *   {
 *     actual,     // la declarada que rige `ahora` (o null)
 *     anterior,   // la que regía antes que ésa (o null)
 *     variacion,  // actual − anterior, a centavos; null si no hay anterior
 *     proximo,    // la primera declarada con `vigente_desde` futuro (o null)
 *     historia,   // las que ya rigieron o rigen, de la más NUEVA a la más
 *                 // vieja, cada una con su `variacion` contra la anterior
 *     total,      // cuántas declaradas hay, rijan o no
 *   }
 *
 * `variacion` es null —no 0— cuando no hay anterior: «sin cambios» afirmaría
 * que el precio no se movió, y lo único que sabemos es que es el primero que
 * tenemos. Con anterior y mismo precio sí es 0, porque ahí es verdad.
 *
 * `proximo` existe porque Carlos puede mandar el mail antes de que rija
 * («desde mañana 00:00»): sin esto la pantalla mostraría el precio viejo como
 * si no hubiera novedad. El próximo NO entra en la historia: todavía no se
 * pagó. */
export function precioDeclarado(filas, ahora = new Date()) {
  const t = instante(ahora);
  const todas = declarados(filas);
  const yaRigen = todas.filter((f) => instante(f.vigente_desde) <= t);
  const actual = yaRigen[yaRigen.length - 1] || null;
  const anterior = yaRigen[yaRigen.length - 2] || null;
  const historia = yaRigen
    .map((f, i) => ({
      ...f,
      // A centavos: en coma flotante 2.305,92 − 2.208 da 97,92000000000007.
      variacion: i > 0 ? centavos(f.precio - yaRigen[i - 1].precio) : null,
    }))
    .reverse();
  return {
    actual,
    anterior,
    variacion: actual && anterior ? centavos(actual.precio - anterior.precio) : null,
    proximo: todas.find((f) => instante(f.vigente_desde) > t) || null,
    historia,
    total: todas.length,
  };
}

/* Los puntos del gráfico escalón: `[{ t, precio }]` en orden de tiempo, uno
 * por declarada que ya rige, y uno más en `ahora` con el precio actual. Ese
 * último es el que le da ancho al escalón vigente: sin él, el precio de hoy
 * sería un punto suelto al final y no se vería cuánto hace que rige. */
export function puntosEscalon(historia, ahora = new Date()) {
  const asc = [...(historia || [])].reverse();
  const puntos = asc.map((f) => ({ t: instante(f.vigente_desde), precio: f.precio }));
  const ultimo = puntos[puntos.length - 1];
  const t = instante(ahora);
  if (ultimo && t > ultimo.t) puntos.push({ t, precio: ultimo.precio });
  return puntos;
}

/* «03/10» en hora argentina, para «al dd/mm». Ilegible: vacío. */
export function diaMesAR(ts) {
  return fechaHoraAR(ts).slice(0, 5);
}

/* ── La cuenta en Otamendi: la planilla entera de Carlos Becerra ───────────
 *
 * Igna, 05/10/2026: «que solo tengamos permiso para ver esos datos Nicolás
 * Cantarini y yo». Carlos Becerra manda por mail («Saldo Otamendi») la
 * planilla de la cuenta de Cantarini en la estación, y FlotaBot la guarda
 * ENTERA en `ypf_saldo.planilla` (ver la migración
 * 20261005040000_ypf_cuenta_otamendi.sql y `ypf_saldo.py` del bot):
 *
 *   {"archivo": "Cantarini_2026.. (1).xlsx",
 *    "hojas": [{"nombre": "Movimientos", "filas": [[…], […]]}, …]}
 *
 * `filas[0]` es la fila 1 y la celda `[i]` la columna A + i. Números como
 * números, fechas como texto ISO («2026-04-01T13:42»), la fórmula sin valor
 * como «=SUM(…)», y {"texto": …} sólo donde un texto se confundiría con una
 * fecha o una fórmula.
 *
 * Tres hojas en el primer mail: «Movimientos» (una fila por carga), un
 * resumen con su TOTAL y unos «CORTE / DIVISOR» al costado, y «SALDO». El
 * saldo NO son pesos —es un crédito en las unidades del resumen, y ni Igna
 * sabe del todo qué es—: se muestra con dos decimales y sin «$».
 *
 * Lo de acá es puro: lee celdas, encuentra la cabecera, separa los pares del
 * costado y arma los movimientos con sus subtotales por mes. La pantalla
 * (`YpfCuenta.jsx`) sólo dibuja. */

/* La llave. NO se asume mientras cargan los permisos: el parpadeo le
 * mostraría la cuenta a quien no la tiene que ver (y le haría pedir la
 * tabla). */
export const PERMISO_CUENTA = "ver.ypf_cuenta";
export const puedeVerCuenta = (permisos) =>
  Boolean(permisos?.includes?.(PERMISO_CUENTA));

/* Cuántas filas de «Movimientos» se dibujan por vez. Son cientos y con los
 * años miles: dibujarlas todas de una traba el teléfono. */
export const MOVIMIENTOS_POR_TANDA = 100;

/* Sin acentos, sin mayúsculas y con los espacios de a uno: «CATEGORÍA» y
 * «Categoria » son la misma cabecera. */
export const normal = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
const DMA = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?: (\d{1,2}):(\d{2}))?$/;

const armarFecha = (anio, mes, dia, hora, min) => {
  const a = Number(anio);
  const m = Number(mes);
  const d = Number(dia);
  if (!(m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const conHora = hora !== undefined && hora !== null;
  const h = conHora ? Number(hora) : 0;
  const mi = conHora ? Number(min) : 0;
  return {
    anio: a,
    mes: m,
    dia: d,
    hora: h,
    min: mi,
    conHora: conHora && (h !== 0 || mi !== 0),
    /* Para ordenar: los números de la fecha tal cual, sin zona. Es hora de
     * la planilla, y se compara contra otras de la misma planilla. */
    t: Date.UTC(a, m - 1, d, h, mi),
    clave: `${a}-${dd(m)}`,
  };
};

/* Una fecha de celda. Con `{ dma: true }` acepta también «01/04/2026 13:42»
 * escrito como texto (por si la planilla trae la fecha de la operación como
 * texto y no como fecha). */
export function fechaDe(v, { dma = false } = {}) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  let m = ISO.exec(s);
  if (m) return armarFecha(m[1], m[2], m[3], m[4], m[5]);
  if (dma && (m = DMA.exec(s))) return armarFecha(m[3], m[2], m[1], m[4], m[5]);
  return null;
}

/* Una celda de `planilla`, con su tipo:
 *   vacia · numero · fecha · bool · formula · texto */
export function celda(v) {
  if (v === null || v === undefined || v === "") return { tipo: "vacia" };
  if (typeof v === "number")
    return Number.isFinite(v) ? { tipo: "numero", valor: v } : { tipo: "vacia" };
  if (typeof v === "boolean") return { tipo: "bool", valor: v };
  if (typeof v === "object") return { tipo: "texto", valor: String(v.texto ?? "") };
  const s = String(v);
  const f = fechaDe(s);
  if (f) return { tipo: "fecha", valor: f };
  if (s.startsWith("=")) return { tipo: "formula", valor: s };
  return { tipo: "texto", valor: s };
}

const vacia = (v) => celda(v).tipo === "vacia";

/* Un número de celda: el número, o un texto que lo es («1.234,50»,
 * «4016,6551», «1234.5»). null si no. */
export function numeroDe(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const s = v.replace(/[$\s]/g, "");
  if (/^-?\d{1,3}(\.\d{3})*(,\d+)?$|^-?\d+,\d+$/.test(s))
    return Number(s.replace(/\./g, "").replace(",", "."));
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

/* «4.016,6551»: miles con punto y decimales con coma, como se lee acá. */
export const numeroAR = (n, { min = 0, max = 4 } = {}) =>
  Number(n).toLocaleString("es-AR", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });

/* Pesos, para el TOTAL de los movimientos. */
export const pesosAR = (n, { min = 2, max = 2 } = {}) =>
  Number(n).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });

/* «01/04/2026» o «01/04/2026 13:42». */
export const fechaTexto = (f) =>
  f
    ? `${dd(f.dia)}/${dd(f.mes)}/${f.anio}` + (f.conHora ? ` ${dd(f.hora)}:${dd(f.min)}` : "")
    : "";

/* Una celda como se lee en la tabla. */
export function textoCelda(v) {
  const c = celda(v);
  if (c.tipo === "vacia") return "";
  if (c.tipo === "numero") return numeroAR(c.valor);
  if (c.tipo === "fecha") return fechaTexto(c.valor);
  if (c.tipo === "bool") return c.valor ? "VERDADERO" : "FALSO";
  return c.valor;
}

/* El saldo de la tarjeta: dos decimales y SIN «$» (no son pesos). */
export const saldoTexto = (n) =>
  n === null || n === undefined || n === "" || !Number.isFinite(Number(n))
    ? "—"
    : numeroAR(Number(n), { min: 2, max: 2 });

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/* «2026-10» → «Octubre 2026». */
export function nombreMes(clave) {
  const [a, m] = String(clave || "").split("-").map(Number);
  return MESES[m - 1] ? `${MESES[m - 1]} ${a}` : "Sin fecha";
}

/* El mes de un momento, en hora argentina: «2026-10». */
export function mesAR(ts) {
  const ms = instante(ts);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms + AR);
  return `${d.getUTCFullYear()}-${dd(d.getUTCMonth() + 1)}`;
}

/* El mes anterior: «2026-01» → «2025-12». */
export function mesAnterior(clave) {
  const [a, m] = clave.split("-").map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${dd(m - 1)}`;
}

/* «A», «B», … «AA». */
export function letra(i) {
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/* ── Las filas de la tabla: qué mails hay ─────────────────────────────────── */

/* Las filas de `ypf_saldo` de Otamendi, de la MÁS NUEVA a la más vieja: por
 * `al`, después por cuándo llegó (`creado_en`) y por `id`. Sin `al` legible
 * no se puede decir de cuándo es: se descarta. El saldo puede ser null (la
 * planilla sin hoja SALDO): esa fila vale igual. */
export function cuentas(filas) {
  const cod = codEess(ESTACION);
  return (filas || [])
    .filter((f) => f && codEess(f.cod_eess) === cod && !Number.isNaN(instante(f.al)))
    .map((f) => ({ ...f, saldo: numeroDe(f.saldo) }))
    .sort(
      (a, b) =>
        instante(b.al) - instante(a.al) ||
        (instante(b.creado_en) || 0) - (instante(a.creado_en) || 0) ||
        (Number(b.id) || 0) - (Number(a.id) || 0),
    );
}

/* ── Una hoja cualquiera ─────────────────────────────────────────────────── */

/* Los «rótulo: valor» del costado (CORTE, DIVISOR…). Se reconocen por la
 * forma y no por el texto, igual que en el bot: a la derecha de la tabla hay
 * una columna que no se usa en NINGÚN renglón, y después exactamente dos
 * columnas, rótulo y valor. Devuelve `{ ancho, pares }`: `ancho` es hasta
 * dónde llega la tabla. Sin esa forma, todo es tabla. */
export function separarPares(filas) {
  const lista = filas || [];
  const ancho = Math.max(0, ...lista.map((f) => (f || []).length));
  const usadas = new Set();
  lista.forEach((f) => (f || []).forEach((v, c) => !vacia(v) && usadas.add(c)));
  let hueco = -1;
  for (let c = 1; c < ancho; c++) {
    if (!usadas.has(c) && [...usadas].some((u) => u < c)) {
      hueco = c;
      break;
    }
  }
  if (hueco < 0) return { ancho, pares: [] };
  const lado = [...usadas].filter((c) => c > hueco).sort((a, b) => a - b);
  if (lado.length !== 2 || lado[1] !== lado[0] + 1) return { ancho, pares: [] };
  const k = lado[0];
  const pares = lista
    .filter((f) => f && !vacia(f[k]))
    .map((f) => ({ rotulo: textoCelda(f[k]), valor: f[k + 1] ?? null }));
  return { ancho: hueco, pares };
}

/* El renglón de títulos: el primero con por lo menos dos textos y casi nada
 * más (fechas y números no son títulos). -1 si no hay. */
export function filaCabecera(filas) {
  return (filas || []).findIndex((f) => {
    const tipos = (f || []).map((v) => celda(v).tipo).filter((t) => t !== "vacia");
    const textos = tipos.filter((t) => t === "texto").length;
    return textos >= 2 && textos >= 0.7 * tipos.length;
  });
}

/* Lo que se dibuja de una hoja cualquiera (el resumen, SALDO):
 *
 *   {
 *     titulos,    // los renglones de arriba de la cabecera, como texto
 *     cabecera,   // los títulos de las columnas (o A, B, C… si no hay)
 *     sinCabecera,
 *     filas,      // [{ n, celdas, total }]: sin las vacías; `n` es la fila
 *     numericas,  // por columna, si casi todo es número (va a la derecha)
 *     pares,      // [{ rotulo, valor }] del costado
 *   }
 *
 * Las columnas que no tienen nada en ninguna fila no se dibujan. Una fila
 * con una celda «TOTAL» va en negrita. */
export function hojaGenerica(filasHoja) {
  const { ancho, pares } = separarPares(filasHoja);
  const recortadas = (filasHoja || []).map((f) => (f || []).slice(0, ancho));
  const iCab = filaCabecera(recortadas);
  const cab = iCab >= 0 ? recortadas[iCab] : null;
  const desde = iCab >= 0 ? iCab + 1 : 0;
  const cuerpo = recortadas
    .map((f, i) => ({ n: i + 1, f }))
    .slice(desde)
    .filter(({ f }) => f.some((v) => !vacia(v)));
  const columnas = [...Array(ancho).keys()].filter(
    (c) => (cab && !vacia(cab[c])) || cuerpo.some(({ f }) => !vacia(f[c])),
  );
  const titulos =
    iCab > 0
      ? recortadas
          .slice(0, iCab)
          .map((f) => f.filter((v) => !vacia(v)).map(textoCelda).join(" · "))
          .filter(Boolean)
      : [];
  return {
    titulos,
    cabecera: columnas.map((c) => (cab ? textoCelda(cab[c]) : letra(c))),
    sinCabecera: !cab,
    filas: cuerpo.map(({ n, f }) => ({
      n,
      celdas: columnas.map((c) => f[c] ?? null),
      total: f.some((v) => celda(v).tipo === "texto" && normal(celda(v).valor) === "total"),
    })),
    numericas: columnas.map((c) => {
      const llenas = cuerpo.map(({ f }) => f[c]).filter((v) => !vacia(v));
      return llenas.length > 0 && llenas.filter((v) => typeof v === "number").length >= llenas.length / 2;
    }),
    pares,
  };
}

/* ── «Movimientos» ───────────────────────────────────────────────────────── */

/* Las columnas que se muestran de entrada, buscadas por el TÍTULO (no por la
 * letra: si Carlos agrega una columna, se corren todas). */
export const COLUMNAS_MOVIMIENTOS = [
  { clave: "fecha", titulo: "Fecha y hora", es: (h) => h.startsWith("fecha y hora") || h === "fecha" },
  { clave: "remito", titulo: "Remito", es: (h) => h === "remito" },
  { clave: "tarjeta", titulo: "Tarjeta", es: (h) => h === "tarjeta" },
  { clave: "producto", titulo: "Producto", es: (h) => h === "producto" },
  { clave: "precio", titulo: "Precio", es: (h) => h === "precio pvp" || h === "precio" },
  { clave: "litros", titulo: "Litros", es: (h) => h === "cantidad" || h === "litros" },
  { clave: "total", titulo: "Total", es: (h) => /^tot(al|\.)? ?pvp$/.test(h) || h === "total" },
];

/* La hoja de movimientos, si lo es: la cabecera (entre los primeros diez
 * renglones) tiene la fecha y por lo menos tres de las otras columnas. null
 * si no: se dibuja como una hoja cualquiera.
 *
 *   {
 *     cabecera,   // los títulos tal cual, todas las columnas
 *     idx,        // { fecha: 0, remito: 2, … } las que se encontraron
 *     vacias,     // Set de columnas sin nada en ninguna fila
 *     filas,      // [{ n, celdas, fecha, mes, litros, total }], la MÁS NUEVA
 *                 // primero (las sin fecha, al final, en su orden)
 *     meses,      // Map «2026-10» → { litros, total, cargas }, para los
 *                 // subtotales y las tarjetas
 *   } */
export function movimientos(filasHoja) {
  const lista = filasHoja || [];
  for (let i = 0; i < Math.min(10, lista.length); i++) {
    const titulos = (lista[i] || []).map((v) => (celda(v).tipo === "texto" ? normal(celda(v).valor) : ""));
    const idx = {};
    COLUMNAS_MOVIMIENTOS.forEach(({ clave, es }) => {
      const c = titulos.findIndex((h) => h && es(h));
      if (c >= 0) idx[clave] = c;
    });
    if (idx.fecha === undefined || Object.keys(idx).length < 4) continue;

    const cabecera = (lista[i] || []).map(textoCelda);
    const filas = lista
      .map((f, j) => ({ n: j + 1, celdas: f || [] }))
      .slice(i + 1)
      .filter(({ celdas }) => celdas.some((v) => !vacia(v)))
      .map(({ n, celdas }) => {
        const fecha = fechaDe(celdas[idx.fecha], { dma: true });
        return {
          n,
          celdas,
          fecha,
          mes: fecha ? fecha.clave : null,
          litros: idx.litros === undefined ? null : numeroDe(celdas[idx.litros]),
          total: idx.total === undefined ? null : numeroDe(celdas[idx.total]),
        };
      });
    filas.sort((a, b) => {
      if (a.fecha && b.fecha) return b.fecha.t - a.fecha.t;
      if (a.fecha || b.fecha) return a.fecha ? -1 : 1;
      return a.n - b.n;
    });

    const anchoTotal = Math.max(cabecera.length, ...filas.map((f) => f.celdas.length));
    const vacias = new Set(
      [...Array(anchoTotal).keys()].filter((c) => filas.every((f) => vacia(f.celdas[c]))),
    );

    const meses = new Map();
    filas.forEach((f) => {
      if (!f.mes) return;
      const m = meses.get(f.mes) || { litros: 0, total: 0, cargas: 0 };
      m.litros += f.litros || 0;
      m.total += f.total || 0;
      m.cargas += 1;
      meses.set(f.mes, m);
    });
    return { cabecera, idx, vacias, filas, meses };
  }
  return null;
}

/* Las tarjetas de arriba: el mes de `ref` (el `al` de la planilla elegida) y
 * el anterior, con litros, pesos y cargas. null si «Movimientos» no tiene
 * las columnas por nombre (fecha, cantidad, total): sin ellas no hay cuenta
 * que hacer, y se dibuja sólo el saldo. Un mes sin cargas va en cero. */
export function resumenMeses(mov, ref) {
  if (!mov || mov.idx.litros === undefined || mov.idx.total === undefined) return null;
  const actual = mesAR(ref) || mesAR(new Date());
  return [actual, mesAnterior(actual)].map((mes) => {
    const m = mov.meses.get(mes) || { litros: 0, total: 0, cargas: 0 };
    return { mes, nombre: nombreMes(mes), ...m };
  });
}
